import {TGetListingRequest} from 'plugins/modules/listing/api/api';
import {useRouter} from 'next/router';
import {useSearchParams} from 'next/navigation';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useListingLoaders} from 'plugins/modules/listing/hooks/useListing/hooks/useListingLoaders';
import {useListingValues} from 'plugins/modules/listing/hooks/useListing/hooks/useListingValues';
import {useListingHandlers} from 'plugins/modules/listing/hooks/useListing/hooks/useListingHandlers';
import {TGetListingTransformedResponse} from 'plugins/modules/listing/types';
import {FiltersFromUrl} from 'plugins/modules/filter/helpers/FiltersFromUrl';
import {FiltersToApi} from 'plugins/modules/filter/helpers/FiltersToApi';
import {getCatalogSection} from 'modules/products/helpers';
import {getTabIdFromUrl} from 'modules/stock-x/helpers/getTabIdFromUrl';

interface ISubscriptionOptions {
    pollingInterval?: number;
    refetchOnReconnect?: boolean;
    refetchOnFocus?: boolean;
}

type TTrigger<Response, Request> = (
    params: Request,
    preferCache?: boolean,
) => Promise<{
    data: Response;
    status: 'pending' | 'fulfilled' | 'rejected';
}> & {
    unsubscribe: () => void;
    abort: () => void;
    unwrap: () => Response;
    refetch: () => void;
    updateSubscriptionOptions: (o: ISubscriptionOptions) => void;
    subscriptionOptions: ISubscriptionOptions;
};

export type TCustomQuery<Response, Request> = () => [
    TTrigger<Response, Request>,
    {
        data?: Response;
        currentData?: Response;
        isUninitialized: false;
        isLoading: false;
        isFetching: false;
        isSuccess: false;
        isError: false;
        originalArgs: Request;
    },
    {
        lastArg: Request;
    },
];

// eslint-disable-next-line complexity,max-lines-per-function
export function useListing<
    Product,
    Response extends TGetListingTransformedResponse<Product>,
    Request extends TGetListingRequest<{
        code?: string;
        section?: string;
        tabId?: string;
        q?: string;
    }> = TGetListingRequest,
>({
    initialData,
    lazyQuery,
    initialArgs,
    dynamicQueryArgs,
}: {
    initialData: Response;
    initialArgs: Request;
    lazyQuery: unknown;
    dynamicQueryArgs?: Partial<Request>;
}) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const [trigger, result] = (lazyQuery as TCustomQuery<Response, Request>)();

    const resolveArgs = useCallback(
        (args: Request): Request => ({
            ...args,
            ...(dynamicQueryArgs ?? {}),
        }),
        [dynamicQueryArgs],
    );

    const mergedInitialArgs = useMemo(() => resolveArgs(initialArgs), [initialArgs, resolveArgs]);

    const getArgsJSON = (args: {filter?: unknown; section?: unknown; tabId?: unknown; q?: unknown}) =>
        JSON.stringify({
            filter: args.filter ?? {},
            section: args.section ?? null,
            tabId: args.tabId ?? null,
            q: args.q ?? null,
        });

    const initialTabId = getTabIdFromUrl(router.asPath, router.query);
    const initialQuery = router.query.q;
    const query =
        // eslint-disable-next-line no-nested-ternary
        'string' === typeof initialQuery ? initialQuery : Array.isArray(initialQuery) ? (initialQuery[0] ?? '') : '';

    const lastAppliedArgsRef = useRef<string>(
        getArgsJSON({
            filter: (initialArgs as unknown as {filter?: unknown})?.filter ?? {},
            section: initialArgs.section ?? null,
            tabId: initialTabId ?? (initialArgs as unknown as {tabId?: unknown})?.tabId ?? null,
            q: (initialArgs as unknown as {q?: unknown})?.q ?? query ?? null,
        }),
    );

    useEffect(() => {
        if (result.isUninitialized) {
            trigger(mergedInitialArgs, true);
            lastAppliedArgsRef.current = getArgsJSON({
                filter: (mergedInitialArgs as unknown as {filter?: unknown})?.filter ?? {},
                section: mergedInitialArgs.section ?? null,
                tabId: initialTabId ?? (mergedInitialArgs as unknown as {tabId?: unknown})?.tabId ?? null,
                q: (mergedInitialArgs as unknown as {q?: unknown})?.q ?? query ?? null,
            });
        }
    }, [trigger, mergedInitialArgs, result.isUninitialized, initialTabId, query]);

    const prevDynamicQueryArgsRef = useRef(dynamicQueryArgs);

    useEffect(() => {
        if (!dynamicQueryArgs) {
            prevDynamicQueryArgsRef.current = dynamicQueryArgs;
            return;
        }

        // запрос временно "неинициализирован" сразу после resetApiState (например, из
        // reservation-флоу при авторизации) — не помечаем args как обработанные, иначе
        // рефетч с новым expressOrderStoreId потеряется навсегда, как только запрос
        // проинициализируется заново
        if (result.isUninitialized) {
            return;
        }

        if (JSON.stringify(prevDynamicQueryArgsRef.current) === JSON.stringify(dynamicQueryArgs)) {
            return;
        }

        prevDynamicQueryArgsRef.current = dynamicQueryArgs;

        // смена dynamicQueryArgs (например включение экспресс-заказа) может сократить
        // выдачу — текущая (не первая) страница может перестать существовать, поэтому
        // при рефетче всегда возвращаемся на страницу 1, а не переиспользуем текущую
        trigger(
            resolveArgs({
                ...((result.originalArgs ?? initialArgs) as Request),
                pageNumber: 1,
                pagination: {isManual: true, firstPage: 1, lastPage: 1},
            }),
            false,
        );
    }, [dynamicQueryArgs, initialArgs, resolveArgs, result.isUninitialized, result.originalArgs, trigger]);

    const loaders = useListingLoaders({
        isFetching: result.isFetching,
        originalArgs: result.originalArgs,
    });

    const dataValues = useListingValues({
        data: result.isUninitialized ? initialData : result.data,
    });

    const hasMoreProductsOnTop = 1 < Number(result?.originalArgs?.pagination?.firstPage || initialArgs.pageNumber);

    const getRequest = useCallback(
        (getArgs: (value: Request) => Request, preferCacheValue: boolean = true) => {
            return trigger(resolveArgs(getArgs(result.originalArgs ?? initialArgs)), preferCacheValue);
        },
        [initialArgs, resolveArgs, result.originalArgs, trigger],
    );

    const fetchData = useCallback(
        async (getArgs: (value: Request) => Request, preferCacheValue: boolean = true) => {
            const {data, status} = await getRequest(getArgs, preferCacheValue);

            return {data, status};
        },
        [getRequest],
    );

    useEffect(() => {
        const {asPath} = router;
        if (!asPath) {
            return;
        }

        try {
            const {filters} = new FiltersFromUrl(asPath);
            const {query: nextFilter} = new FiltersToApi(filters);
            const tabId = getTabIdFromUrl(asPath, router.query);

            const catalogSectionQuery = getCatalogSection(router.query.slug);
            const sectionQuery = router.query.section;
            const queryQ = router.query.q;
            const q =
                // eslint-disable-next-line no-nested-ternary
                'string' === typeof queryQ ? queryQ : Array.isArray(queryQ) ? (queryQ[0] ?? '') : '';

            // eslint-disable-next-line no-nested-ternary
            const section = catalogSectionQuery
                ? catalogSectionQuery
                : sectionQuery === undefined
                  ? null
                  : sectionQuery;

            const nextFilterJSON = getArgsJSON({
                filter: nextFilter ?? {},
                section,
                tabId,
                q,
            });

            if (lastAppliedArgsRef.current === nextFilterJSON) {
                return;
            }

            lastAppliedArgsRef.current = nextFilterJSON;

            fetchData((args) => {
                return {
                    ...(args as unknown as Request),
                    filter: nextFilter as unknown as Request['filter'],
                    section,
                    tabId,
                    q,
                    pageNumber: 1,
                    pagination: {
                        isManual: true,
                        firstPage: 1,
                        lastPage: 1,
                    },
                };
            }, true);
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.asPath]);

    const handlers = useListingHandlers({
        router,
        searchParams,
        fetchData,
    });

    return {
        ...loaders,
        ...handlers,
        ...dataValues,
        hasMoreProductsOnTop,
    };
}
